'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CasesDashboardView } from './Entries/CasesDashboardView'
import { SourceLinesTopRow, type SourceLine } from './charts/SourceLinesTopRow'
import { TakedownBySourceCard, type TakedownEntry, type TakedownSource } from './charts'
import { ReplayControl, type ReplayStatus } from './ReplayControl'
import { useDashboardRange } from './dashboardRange'
import { baseDashboardClasses } from './BaseDashboard'
import { apiGet } from './Entries/entriesApi'
import type { EntryRow } from './Entries/entryTypes'
import type { ProcessedTheme } from './ProcessedEntry/DomainEntry/processedTypes'
import { parseDbTimestamp } from '@/lib/dates'

// CTT's vendor feed, the data TrustFence is compared against: every entry with
// source "ctt_trustfence_email" (routes/cloudflareWorkers/brands/ctt.ts). The
// table is the source-scoped Cases table; the header works like SIBS's Custom
// Mail Alert one (CampaignsView): a TimeRangePicker the table and charts
// follow, a Table/Range toggle for what the charts count, and a replay that
// drip-feeds the range's entries into the table oldest first while the
// charts grow day by day.
const VENDOR_SOURCE = 'ctt_trustfence_email'

// The top charts' lines, one per source, in legend order. slot = categorical
// colour (0 blue, 1 orange, 2 aqua, …), fixed per source. A new source also
// needs its entry in the backend's VENDOR_SOURCE_DASHBOARD_KEYS allow-list
// (fraud-mono-repo routes/backoffice/postManagement.ts), or its fetch is refused.
const PILOT_SERIES: Array<{ key: string; label: string; source: string; slot: number }> = [
  { key: 'vendor', label: 'CTT vendor', source: VENDOR_SOURCE, slot: 0 },
]

function localDayKey(iso: string): string | null {
  const d = parseDbTimestamp(iso)
  if (!d) return null
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function CttVendorEntriesView({ theme, isEditor }: { theme: ProcessedTheme; isEditor?: boolean }) {
  const { isDark, border, muted } = baseDashboardClasses(theme)

  // The shared dashboard range: last 30 days unless this dashboard's cookie
  // remembers another choice.
  const { from: chartFrom, to: chartTo, setRange: setChartRange } = useDashboardRange('ctt-pilot-overview')
  const [chartCoupled, setChartCoupled] = useState(false)

  // Local-day bounds as instants: [from 00:00, day after `to` 00:00).
  const range = useMemo(() => {
    const until = new Date(chartTo + 'T00:00')
    until.setDate(until.getDate() + 1)
    return { since: new Date(chartFrom + 'T00:00').toISOString(), until: until.toISOString() }
  }, [chartFrom, chartTo])

  const [replayOpen, setReplayOpen] = useState(false)
  const [replayStatus, setReplayStatus] = useState<ReplayStatus>('idle')
  const [replayQueue, setReplayQueue] = useState<EntryRow[]>([])
  const [replayIndex, setReplayIndex] = useState(0)
  const [replayRate, setReplayRate] = useState(10)
  const [replayRows, setReplayRows] = useState<EntryRow[] | null>(null)
  const [replayMaxDay, setReplayMaxDay] = useState<string | null>(null)
  const replayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isReplaying = replayStatus !== 'idle'
  const replayControlsLocked = replayStatus === 'loading' || replayStatus === 'playing' || replayStatus === 'paused'

  // Replay tick: drip-feed one entry per interval, newest on top.
  useEffect(() => {
    if (replayStatus !== 'playing') {
      if (replayTimerRef.current) clearTimeout(replayTimerRef.current)
      return
    }
    if (replayIndex >= replayQueue.length) {
      setReplayStatus('done')
      return
    }
    const delay = 1000 / Math.max(0.1, replayRate)
    replayTimerRef.current = setTimeout(() => {
      const entry = replayQueue[replayIndex]
      setReplayRows((prev) => [entry, ...(prev ?? [])])
      const day = localDayKey((entry.raw_entries ?? entry).created_at)
      if (day) setReplayMaxDay(day)
      setReplayIndex((i) => i + 1)
    }, delay)
    return () => { if (replayTimerRef.current) clearTimeout(replayTimerRef.current) }
  }, [replayStatus, replayIndex, replayQueue, replayRate])

  const startReplay = useCallback(async () => {
    setReplayOpen(false)
    setReplayStatus('loading')
    setReplayRows([])
    setReplayQueue([])
    setReplayIndex(0)
    setReplayMaxDay(null)
    setChartCoupled(true)
    const params = new URLSearchParams({ page: '0', all: '1', source: VENDOR_SOURCE, since: range.since, until: range.until, hide_rejected: '1' })
    const result = await apiGet<{ entries: EntryRow[] }>(`/cases?${params}`)
    if (!result.success || !result.data) {
      setReplayRows(null)
      setChartCoupled(false)
      setReplayStatus('idle')
      return
    }
    const q = result.data.entries ?? []
    setReplayQueue(q)
    setReplayStatus(q.length > 0 ? 'playing' : 'done')
  }, [range])

  const stopReplay = useCallback(() => {
    if (replayTimerRef.current) clearTimeout(replayTimerRef.current)
    setReplayStatus('idle')
    setReplayQueue([])
    setReplayMaxDay(null)
    setReplayIndex(0)
    setChartCoupled(false)
    setReplayRows(null)
  }, [])

  // A finished replay stays on screen until the range or the toggle changes.
  const clearCompletedReplay = useCallback(() => {
    if (replayStatus !== 'done') return
    setReplayStatus('idle')
    setReplayOpen(false)
    setReplayQueue([])
    setReplayMaxDay(null)
    setReplayIndex(0)
    setReplayRows(null)
  }, [replayStatus])

  const applyTimeRange = useCallback((from: string, to: string) => {
    clearCompletedReplay()
    setChartRange(from, to)
    setChartCoupled(false)
  }, [clearCompletedReplay, setChartRange])

  useEffect(() => {
    if (replayStatus === 'loading' || replayStatus === 'playing') setReplayOpen(false)
  }, [replayStatus])

  return (
    <CasesDashboardView
      theme={theme}
      sourceType={VENDOR_SOURCE}
      expectedPlatformCode="ctt"
      storageKey="ctt-vendor-entries"
      isEditor={isEditor}
      eyebrow="CTT · Pilot"
      title="Pilot Overview"
      subtitle={`CTT's vendor feed (source "${VENDOR_SOURCE}"), the data TrustFence is compared against.`}
      range={range}
      showIdColumns
      deepLink
      hideRejected
      replayRows={replayRows}
      timeRange={{ from: chartFrom, to: chartTo, onApply: applyTimeRange, disabled: replayControlsLocked }}
      headerActions={
        <>
          <button
            onClick={() => {
              clearCompletedReplay()
              setChartCoupled((v) => !v)
            }}
            disabled={replayControlsLocked}
            title={chartCoupled ? 'Using table filters — click to use the selected time range' : 'Using selected time range — click to couple charts to the table'}
            className={`flex h-7 items-center gap-1.5 border px-2.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-40 ${
              chartCoupled
                ? 'border-signal bg-signal-surface text-signal'
                : `${border} ${muted} hover:border-signal hover:text-signal`
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${chartCoupled ? 'bg-signal' : 'bg-muted'}`} />
            {chartCoupled ? 'Table' : 'Range'}
          </button>
          <ReplayControl
            status={replayStatus}
            open={replayOpen}
            onOpenChange={setReplayOpen}
            from={chartFrom}
            to={chartTo}
            rate={replayRate}
            onRateChange={setReplayRate}
            index={replayIndex}
            queueLength={replayQueue.length}
            onPlay={startReplay}
            onPause={() => setReplayStatus('paused')}
            onResume={() => setReplayStatus('playing')}
            onStop={stopReplay}
            isDark={isDark}
            border={border}
            muted={muted}
          />
        </>
      }
      renderWidgets={({ isDark: dark, refreshKey, filtered }) => (
        <PilotTopRowData
          from={chartFrom}
          to={chartTo}
          range={range}
          coupledRows={chartCoupled ? filtered : null}
          revealThrough={isReplaying ? replayMaxDay : null}
          refreshKey={refreshKey}
          isDark={dark}
        />
      )}
    />
  )
}

// Range mode counts each source's entries over the whole period (fetched
// here, not the table's loaded pages). Table mode (and replay) switches the
// table's own source to the rows the table shows, after its search filter;
// other sources keep counting their range.
function PilotTopRowData({
  from,
  to,
  range,
  coupledRows,
  revealThrough,
  refreshKey,
  isDark,
}: {
  from: string
  to: string
  range: { since: string; until: string }
  coupledRows: EntryRow[] | null
  revealThrough: string | null
  refreshKey: number
  isDark: boolean
}) {
  const [fetched, setFetched] = useState<Record<string, { timestamps: string[]; error: string | null }>>({})
  const [loading, setLoading] = useState(false)
  const [takedowns, setTakedowns] = useState<Record<string, { entries: TakedownEntry[]; error: string | null }>>({})
  const [takedownsLoading, setTakedownsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all(PILOT_SERIES.map(async (s) => {
      const params = new URLSearchParams({ source: s.source, since: range.since, until: range.until, hide_rejected: '1' })
      const result = await apiGet<{ timestamps: string[] }>(`/source-entry-timestamps?${params}`)
      return [s.key, result.success && result.data
        ? { timestamps: result.data.timestamps, error: null }
        : { timestamps: [], error: result.error ?? 'Failed to load' }] as const
    })).then((pairs) => {
      if (cancelled) return
      setFetched(Object.fromEntries(pairs))
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [range.since, range.until, refreshKey])

  // Time to takedown always counts the range, whatever the Table/Range toggle
  // or a replay shows.
  useEffect(() => {
    let cancelled = false
    setTakedownsLoading(true)
    Promise.all(PILOT_SERIES.map(async (s) => {
      const params = new URLSearchParams({ source: s.source, since: range.since, until: range.until, hide_rejected: '1' })
      const result = await apiGet<{ entries: TakedownEntry[] }>(`/source-takedown-times?${params}`)
      return [s.key, result.success && result.data
        ? { entries: result.data.entries, error: null }
        : { entries: [], error: result.error ?? 'Failed to load' }] as const
    })).then((pairs) => {
      if (cancelled) return
      setTakedowns(Object.fromEntries(pairs))
      setTakedownsLoading(false)
    })
    return () => { cancelled = true }
  }, [range.since, range.until, refreshKey])

  const takedownSources = useMemo<TakedownSource[]>(() => PILOT_SERIES.map((s) => ({
    key: s.key,
    label: s.label,
    slot: s.slot,
    entries: takedowns[s.key]?.entries ?? [],
    loading: takedownsLoading,
    error: takedowns[s.key]?.error ?? null,
  })), [takedowns, takedownsLoading])

  const series = useMemo<SourceLine[]>(() => PILOT_SERIES.map((s) => {
    if (coupledRows && s.source === VENDOR_SOURCE) {
      return {
        key: s.key,
        label: s.label,
        slot: s.slot,
        timestamps: coupledRows.map((e) => (e.raw_entries ?? e).created_at).filter((v): v is string => typeof v === 'string'),
      }
    }
    return { key: s.key, label: s.label, slot: s.slot, timestamps: fetched[s.key]?.timestamps ?? [], loading, error: fetched[s.key]?.error ?? null }
  }), [coupledRows, fetched, loading])

  return (
    <>
      <SourceLinesTopRow from={from} to={to} series={series} revealThrough={revealThrough} isDark={isDark} />
      <TakedownBySourceCard sources={takedownSources} isDark={isDark} />
    </>
  )
}
